/**
 * De Web Speech API, met de scherpe randen eraf.
 *
 * Deze API is per browser anders en op sommige plekken ronduit onbetrouwbaar.
 * Alles wat daarmee te maken heeft, staat in dit bestand, zodat de rest van de
 * app er niet over hoeft na te denken.
 */

interface HerkenningsResultaat {
  readonly isFinal: boolean;
  readonly length: number;
  [index: number]: { readonly transcript: string; readonly confidence: number };
}

interface HerkenningsLijst {
  readonly length: number;
  [index: number]: HerkenningsResultaat;
}

export interface HerkenningsGebeurtenis {
  readonly resultIndex: number;
  readonly results: HerkenningsLijst;
}

export interface SpraakHerkenner {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  maxAlternatives: number;
  onresult: ((e: HerkenningsGebeurtenis) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

type HerkennerBouwer = new () => SpraakHerkenner;

interface VensterMetSpraak {
  SpeechRecognition?: HerkennerBouwer;
  webkitSpeechRecognition?: HerkennerBouwer;
}

export function herkennerBeschikbaar(): boolean {
  const v = window as unknown as VensterMetSpraak;
  return v.SpeechRecognition !== undefined || v.webkitSpeechRecognition !== undefined;
}

export function maakHerkenner(): SpraakHerkenner | null {
  const v = window as unknown as VensterMetSpraak;
  const Bouwer = v.SpeechRecognition ?? v.webkitSpeechRecognition;
  return Bouwer === undefined ? null : new Bouwer();
}

/**
 * Bouwt de tekst op uit een resultatenlijst.
 *
 * DIT REPAREERT EEN BUG UIT HET OUDE CONCEPT. Daar liep de lus achterstevoren
 * en stopte bij het EERSTE finale resultaat:
 *
 *     for (let i = event.results.length - 1; i >= 0; i--) {
 *       if (event.results[i].isFinal) { transcript = ...; break; }
 *       transcript += ...;
 *     }
 *
 * Met continuous = true levert de browser MEERDERE finale stukken op, één per
 * pauze in je zin. Die lus pakte alleen het laatste stuk. Spreek je een lange
 * zin met een adempauze erin, dan verdween het begin - zonder foutmelding, en
 * je merkt het pas als de vertaling nergens op slaat.
 *
 * Nu: alle finale stukken in volgorde, plus het lopende stuk erachter.
 *
 * EN EEN TWEEDE FOUT, GEMETEN OP EEN PIXEL 9A OP 4 SEPTEMBER 2026. Chrome op
 * Android zet elk tussenresultaat als een NIEUWE regel in de lijst, telkens
 * een woord langer:
 *
 *     "Dit"  "Dit is"  "Dit is een"  "Dit is een test"  ...
 *
 * Die achter elkaar plakken gaf onleesbare brij. Op de Mac viel het niet op,
 * want daar staat er maar EEN tussenresultaat in de lijst.
 *
 * Daarom: van de NIET-finale stukken telt alleen de LAATSTE. Tussenresultaten
 * zijn opeenvolgende verbeteringen van dezelfde lopende zin, geen losse delen.
 * De finale stukken worden wel allemaal bewaard - die zijn af en volgen elkaar
 * echt op.
 */
export function bouwTranscript(results: HerkenningsLijst): { vast: string; lopend: string } {
  const vaste: string[] = [];
  let lopend = '';

  for (let i = 0; i < results.length; i++) {
    const resultaat = results[i];
    if (resultaat === undefined) continue;
    const tekst = (resultaat[0]?.transcript ?? '').trim();
    if (tekst === '') continue;

    if (resultaat.isFinal) {
      vaste.push(tekst);
    } else {
      // Overschrijven, niet aanvullen. Zie de toelichting hierboven.
      lopend = tekst;
    }
  }

  return { vast: vaste.join(' ').trim(), lopend };
}

/** Foutcodes van de browser, vertaald naar iets waar een mens wat aan heeft. */
export function spraakFoutTekst(code: string): string {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'De microfoon is geblokkeerd. Geef toestemming in je browser.';
    case 'no-speech':
      return 'Niets gehoord. Probeer het nog eens.';
    case 'audio-capture':
      return 'Geen microfoon gevonden.';
    case 'network':
      return 'De spraakherkenning kon de server niet bereiken.';
    case 'aborted':
      return 'De opname is afgebroken.';
    default:
      return 'De spraakherkenning werkte niet.';
  }
}

/** Kan deze browser audio opnemen? Nodig voor het meesturen van je eigen stem. */
export function opnemenBeschikbaar(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    navigator.mediaDevices !== undefined &&
    typeof navigator.mediaDevices.getUserMedia === 'function'
  );
}

/**
 * De bitsnelheid van een opname: 32 kbit/s, ruim genoeg voor spraak.
 *
 * WAAROM DIT VAST STAAT. Zonder deze instelling kiest de browser zelf, en de
 * iPhone kiest hoog: gemeten op 12 september 2026, 53 kB voor twee seconden
 * opus, dus ongeveer 26 kB per seconde. Met de base64-verpakking erbij zat een
 * opname na dertig tot veertig seconden al aan de grens van 1,5 MB, en dan
 * zei de app "te lang" waar hij "te groot" bedoelde. Giovanni kon op zijn
 * iPhone geen zin van meer dan een halve minuut meer inspreken, terwijl de
 * Mac de volle minuut haalde. Op 32 kbit/s is een minuut ongeveer 240 kB.
 *
 * De vertaling luistert naar stem, niet naar hifi; 32 kbit/s opus is wat
 * telefonie en spraakberichten ook gebruiken.
 */
export const OPNAME_BITS_PER_SECONDE = 32_000;

/**
 * De instellingen voor een MediaRecorder: het beste formaat plus de vaste
 * bitsnelheid. EEN plek, voor het gesprek en de meetpagina samen, zodat een
 * meting hetzelfde meet als de app doet.
 */
export function opnameOpties(): MediaRecorderOptions {
  const formaat = besteOpnameFormaat();
  return formaat !== null
    ? { mimeType: formaat, audioBitsPerSecond: OPNAME_BITS_PER_SECONDE }
    : { audioBitsPerSecond: OPNAME_BITS_PER_SECONDE };
}

/** Het eerste formaat dat deze browser aankan. Opus is klein; wav is groot. */
export function besteOpnameFormaat(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const type of [
    'audio/webm;codecs=opus',
    'audio/ogg;codecs=opus',
    'audio/mp4',
    'audio/webm',
  ]) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return null;
}
